import { Component, HostListener, OnDestroy, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { AuthService } from './core/auth.service';
import { PortalAnalyticsService } from './core/portal-analytics.service';
import { PortalApiService } from './core/portal-api.service';
import { PortalModule } from './core/portal.models';

interface PortalNavItem {
  key: string;
  label: string;
  description: string;
  route: string;
  icon: string;
  accent: string;
  group: string;
}

interface PortalNavGroup {
  key: string;
  label: string;
  items: PortalNavItem[];
}

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, RouterLink],
  templateUrl: './app.html',
  styleUrl: './app.css',
})
export class App implements OnDestroy {
  private readonly portalApi = inject(PortalApiService);
  private readonly analytics = inject(PortalAnalyticsService);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly routerSubscription: Subscription;

  readonly session = this.portalApi.session;
  readonly commandMenuVisible = signal(false);
  readonly mobileNavigationVisible = signal(false);
  readonly sidebarCollapsed = signal(false);
  readonly applicationSearchTerm = signal('');
  readonly clientLogoFailed = signal(false);
  readonly currentUrl = signal(this.router.url);
  readonly navItems = computed(() => this.buildNavItems(this.session()?.modules ?? []));
  readonly navGroups = computed(() => this.groupNavItems(this.navItems()));
  readonly visibleLauncherItems = computed(() => this.filterNavItems(this.navItems(), this.applicationSearchTerm()));
  readonly currentPageTitle = computed(() => this.resolveCurrentPageTitle());
  readonly workspaceName = computed(() => this.session()?.scope.crmClientName || this.session()?.tenant.name || 'Customer workspace');

  constructor() {
    this.routerSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(event => {
        this.currentUrl.set(event.urlAfterRedirects);
        this.closeOverlays();
        this.analytics.track('workspace_viewed', { route: this.normalizeUrl(event.urlAfterRedirects) });
      });

    this.auth.hasCurrentUser().then(hasUser => {
      if (hasUser) {
        this.portalApi.loadSession().subscribe({ error: () => this.router.navigate(['/login']) });
      }
    });
  }

  ngOnDestroy(): void { this.routerSubscription.unsubscribe(); }

  openCommandMenu(): void {
    this.commandMenuVisible.set(true);
    this.mobileNavigationVisible.set(false);
    this.analytics.track('application_launcher_opened');
  }

  toggleMobileNavigation(): void {
    this.mobileNavigationVisible.update(value => !value);
    this.commandMenuVisible.set(false);
  }

  toggleSidebar(): void { this.sidebarCollapsed.update(value => !value); }

  closeOverlays(): void {
    this.commandMenuVisible.set(false);
    this.mobileNavigationVisible.set(false);
    this.applicationSearchTerm.set('');
  }

  @HostListener('document:keydown.escape')
  onEscape(): void { this.closeOverlays(); }

  @HostListener('document:keydown', ['$event'])
  onShortcut(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.commandMenuVisible() ? this.closeOverlays() : this.openCommandMenu();
    }
  }

  onApplicationSearchChange(value: string): void { this.applicationSearchTerm.set(value); }
  clearApplicationSearch(): void { this.applicationSearchTerm.set(''); }

  companyInitial(): string { return (this.session()?.tenant.name?.trim() || 'Customer').charAt(0).toUpperCase(); }
  clientInitial(): string { return (this.workspaceName().trim() || 'Client').charAt(0).toUpperCase(); }
  userInitial(): string {
    const profile = this.session()?.profile;
    return (profile?.name?.trim() || profile?.emailAddress?.trim() || 'User').charAt(0).toUpperCase();
  }
  primaryRoleLabel(): string { return this.session()?.roles.find(role => !!role?.trim()) ?? 'Portal user'; }

  isActive(item: PortalNavItem): boolean {
    const current = this.normalizeUrl(this.currentUrl());
    return current === item.route || (item.route !== '/dashboard' && current.startsWith(`${item.route}/`));
  }

  logout(): void {
    this.auth.logout().subscribe(() => {
      this.portalApi.clearSession();
      this.router.navigate(['/login']);
    });
  }

  private buildNavItems(modules: PortalModule[]): PortalNavItem[] {
    const enabled = modules.filter(module => module.enabled && module.key !== 'dashboard');
    const permissions = new Set(this.session()?.permissions ?? []);
    const items: PortalNavItem[] = [this.item('overview', 'Overview', 'Your priorities, recent activity and quick actions.', '/dashboard', 'fa-solid fa-house', 'teal', 'Overview')];

    for (const module of enabled) {
      if (module.key === 'finance') {
        const financeRoutes = [
          ['statements', 'Statements', 'Review account statements and balances.', 'fa-regular fa-file-lines'],
          ['invoices', 'Invoices', 'Open invoices and customer documents.', 'fa-solid fa-file-invoice'],
          ['bills', 'Bills', 'Review bills and their current status.', 'fa-solid fa-receipt'],
          ['receipts', 'Receipts', 'Find receipts and payment references.', 'fa-solid fa-money-bill-transfer'],
        ] as const;
        financeRoutes.forEach(([key, label, description, icon]) => {
          if (permissions.has(`portal.finance.${key}.view`)) {
            items.push(this.item(`finance-${key}`, label, description, `/finance/${key}`, icon, 'violet', 'Finance'));
          }
        });
        continue;
      }

      items.push(this.item(
        module.key,
        module.label,
        this.moduleHint(module.key),
        module.route,
        this.iconForModule(module.key),
        this.accentForModule(module.key),
        this.groupForModule(module.key),
      ));
    }

    items.push(this.item('help', 'Help & guides', 'Guidance for every workspace and task.', '/help', 'fa-regular fa-circle-question', 'orange', 'Support'));
    return items;
  }

  private item(key: string, label: string, description: string, route: string, icon: string, accent: string, group: string): PortalNavItem {
    return { key, label, description, route, icon, accent, group };
  }

  private groupNavItems(items: PortalNavItem[]): PortalNavGroup[] {
    const order = ['Overview', 'Track', 'Orders & projects', 'Inventory', 'Finance', 'Support'];
    return order.map(label => ({ key: label.toLowerCase().replaceAll(' ', '-').replace('&', 'and'), label, items: items.filter(item => item.group === label) }))
      .filter(group => group.items.length > 0);
  }

  private filterNavItems(items: PortalNavItem[], term: string): PortalNavItem[] {
    const query = term.trim().toLowerCase();
    if (!query) return items;
    return items.filter(item => [item.label, item.description, item.group].some(value => value.toLowerCase().includes(query)));
  }

  private resolveCurrentPageTitle(): string {
    const current = this.normalizeUrl(this.currentUrl());
    const match = this.navItems().find(item => current === item.route || (item.route !== '/dashboard' && current.startsWith(`${item.route}/`)));
    return match?.label ?? 'Customer portal';
  }

  private normalizeUrl(url: string): string {
    const path = url.split('?')[0].split('#')[0] || '/';
    return path.endsWith('/') && path.length > 1 ? path.slice(0, -1) : path;
  }

  private groupForModule(key: string): string {
    if (['shipments', 'container-tracking'].includes(key)) return 'Track';
    if (['clearance', 'order-releases', 'quotations', 'posm'].includes(key)) return 'Orders & projects';
    if (key === 'warehousing') return 'Inventory';
    return 'Support';
  }

  private iconForModule(key: string): string {
    return ({
      shipments: 'fa-solid fa-truck-fast', clearance: 'fa-solid fa-file-shield',
      'container-tracking': 'fa-solid fa-box', 'order-releases': 'fa-solid fa-clipboard-list',
      quotations: 'fa-solid fa-file-signature', posm: 'fa-solid fa-layer-group',
      warehousing: 'fa-solid fa-warehouse', support: 'fa-regular fa-life-ring',
    } as Record<string, string>)[key] ?? 'fa-solid fa-cube';
  }

  private accentForModule(key: string): string {
    return ({ shipments:'blue', clearance:'orange', 'container-tracking':'violet', 'order-releases':'blue', quotations:'violet', posm:'purple', warehousing:'teal', support:'orange' } as Record<string,string>)[key] ?? 'teal';
  }

  private moduleHint(key: string): string {
    return ({
      shipments: 'Track delivery progress, documents and exceptions.',
      support: 'Create a request and continue the conversation.',
      posm: 'Follow materials, delivery plans and store rollout progress.',
      warehousing: 'Review availability, stock units and movement history.',
      quotations: 'Review proposals, documents, comments and approvals.',
      clearance: 'Review clearance records and supporting documents.',
      'container-tracking': 'Follow container status and associated documents.',
      'order-releases': 'Review releases, routes and linked documents.',
    } as Record<string,string>)[key] ?? 'Open this customer workspace.';
  }
}
